<template>
  <div class="knowledge-courses-page">
    <ImageBar title="知识课程" height="80px"/>
    
    <div class="courses-container">
      <!-- 筛选和查找区域 -->
      <div class="filter-section">
        <div class="search-box">
          <el-input
            v-model="searchKeyword"
            placeholder="查找课程名称或描述..."
            prefix-icon="Search"
            clearable
            @input="handleSearch"
            style="width: 300px;"
          />
        </div>
        
        <div class="filter-controls">
          <el-select 
            v-model="selectedCategory" 
            placeholder="课程类型" 
            clearable
            @change="handleFilter"
            style="width: 150px; margin-right: 15px;"
          >
            <el-option
              v-for="category in categories"
              :key="category"
              :label="category"
              :value="category"
            />
          </el-select>
          
          <el-select 
            v-model="selectedLevel" 
            placeholder="难度" 
            clearable
            @change="handleFilter"
            style="width: 120px; margin-right: 15px;"
          >
            <el-option label="初级" value="1" />
            <el-option label="中级" value="2" />
            <el-option label="高级" value="3" />
          </el-select>

          <div class="filter-actions">
            <el-tooltip content="刷新列表" placement="top">
              <el-button circle :loading="refreshing" aria-label="刷新列表" @click="refreshCourses">
                <el-icon><Refresh /></el-icon>
              </el-button>
            </el-tooltip>
            <el-tooltip content="复位筛选" placement="top">
              <el-button circle aria-label="复位筛选" @click="resetFilters">
                <el-icon><RefreshLeft /></el-icon>
              </el-button>
            </el-tooltip>
          </div>
        </div>
      </div>
      <ContentTagFilterBar v-model="selectedTagId" scope="course" @update:model-value="handleFilter" />
      
      <div v-if="loading && courses.length === 0" class="loading-container">
        <el-skeleton :rows="3" animated />
      </div>
      
      <div v-else-if="filteredCourses.length === 0" class="empty-state">
        <el-empty description="暂无课程" />
      </div>
      
      <div v-else class="courses-grid">
        <div 
          v-for="course in paginatedCourses" 
          :key="course.kcId" 
          class="course-card"
          :class="{ 'purchased': course.isAcquired > 0 }"
          @click="viewCourse(course)"
        >
          <div class="course-image">
            <img
              class="course-banner"
              :src="getCourseBanner(course)"
              alt="课程封面"
            />
            <div class="course-cover">
              <span class="course-icon"></span>
            </div>
          </div>
          <div class="course-content">
            <div class="course-title-row">
              <h3 class="course-title">{{ course.name }}</h3>
              <el-tag :type="getLevelTagType(course.level)" size="small">
                {{ getLevelText(course.level) }}
              </el-tag>
              <el-tag type="info" size="small">{{ course.category }}</el-tag>
              <el-tag
                v-for="tag in (course.tags || [])"
                :key="tag.tagId"
                size="small"
                class="content-tag"
                @click.stop="selectCourseTag(tag.tagId)"
              >{{ tag.name }}</el-tag>
            </div>
            <p v-if="course.description" class="course-description">{{ course.description }}</p>
            
            <div class="course-meta">
              <div class="meta-item">
                <el-icon><Clock /></el-icon>
                <span>{{ getCourseDuration(course) }} 分钟</span>
              </div>
              <div class="meta-item">
                <el-icon><Document /></el-icon>
                <span>{{ getKnowledgePointCount(course) }} 个知识点</span>
              </div>
            </div>
            
            <div class="course-footer">
              <div class="course-price">
                <span class="price-label">价格:</span>
                <span class="price-value" :class="{ 'free': !course.fee || course.fee === 0 }">
                  {{ formatPrice(course.fee) }}
                  <span v-if="course.isAcquired > 0" class="purchased-label">{{ course.acquireSource === 'redeem' ? '（已兑换）' : '（已购买）' }}</span>
                </span>
              </div>
              <div class="course-actions">
                <el-button 
                  type="primary" 
                  size="small" 
                  @click.stop="viewCourse(course)"
                >
                  {{ courseActionLabel(course) }}
                </el-button>
                <el-button
                  v-if="canRedeem(course)"
                  size="small"
                  @click.stop="openRedeem(course)"
                >兑换码</el-button>
                <el-button type="info" size="small" @click.stop="showCoursePreview(course)">
                  大纲
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 分页 -->
      <AdminPagination
        v-if="totalPages > 1"
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :page-sizes="[12, 24, 36, 48]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="filteredCourses.length"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
    
    <!-- 课程大纲对话框 -->
    <el-dialog
      v-model="previewDialogVisible"
      title="课程大纲"
      width="60%"
      :before-close="handlePreviewClose"
    >
      <div v-if="previewCourse" class="course-preview">
        <div class="preview-cover">
          <img
            class="preview-banner"
            :src="getCourseBanner(previewCourse)"
            alt="课程封面"
          />
        </div>
        <div class="preview-header">
          <div class="preview-title-row">
            <h3>{{ previewCourse.name }}</h3>
            <el-tag :type="getLevelTagType(previewCourse.level)" size="small">
              {{ getLevelText(previewCourse.level) }}
            </el-tag>
            <el-tag type="info" size="small">{{ previewCourse.category }}</el-tag>
            <el-tag
              v-for="tag in (previewCourse.tags || [])"
              :key="tag.tagId"
              size="small"
            >{{ tag.name }}</el-tag>
          </div>
          <div class="preview-price">
            <span class="price-label">价格:</span>
            <span class="price-value" :class="{ 'free': !previewCourse.fee || previewCourse.fee === 0 }">
              {{ formatPrice(previewCourse.fee) }}
              <span v-if="previewCourse.isAcquired > 0" class="purchased-label">{{ previewCourse.acquireSource === 'redeem' ? '（已兑换）' : '（已购买）' }}</span>
            </span>
          </div>
        </div>

        <div class="preview-description">
          <h4>课程描述</h4>
          <p v-if="previewCourse.description">{{ previewCourse.description }}</p>
        </div>

        <div class="preview-structure">
          <h4>课程大纲</h4>
          <div v-if="previewCourse.kpTree && previewCourse.kpTree.children" class="structure-tree">
            <div v-for="(node, index) in previewCourse.kpTree.children" :key="index" class="structure-node">
              <div class="node-header">
                <span class="node-icon">📖</span>
                <span class="node-name">{{ node.name || '未命名知识点' }}</span>
                <span class="node-seq">{{ node.seqname }}</span>
              </div>
              <div v-if="node.children && node.children.length" class="node-children">
                <div v-for="(child, childIndex) in node.children" :key="childIndex" class="child-node">
                  <span class="child-icon">📝</span>
                  <span class="child-name">{{ child.name || '未命名知识点' }}</span>
                  <span class="child-seq">{{ child.seqname }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-structure">
            <p>暂无课程结构信息</p>
          </div>
        </div>
      </div>
      
      <template #footer>
        <el-button @click="previewDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="redeemVisible" title="核销兑换码" width="420px">
      <p class="redeem-hint">把创作者发给你的兑换码填在这里。核销后可以学习。</p>
      <el-input v-model="redeemCode" placeholder="兑换码" maxlength="32" @keyup.enter="submitRedeem" />
      <template #footer>
        <el-button @click="redeemVisible = false">取消</el-button>
        <el-button type="primary" :loading="redeeming" @click="submitRedeem">核销</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Clock, Document, Refresh, RefreshLeft } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import ImageBar from '@/components/ImageBar.vue'
import AdminPagination from '@/components/AdminPagination.vue'
import ContentTagFilterBar from '@/components/ContentTagFilterBar.vue'
import { promptLogin, loginPageRoute, isLoggedIn, goToLogin } from '@/utils/loginFlow'
import { isFreeCourse, coursePrimaryActionLabel } from '@/utils/courseAccess'
import { couponFromRouteQuery, withCouponQuery } from '@/utils/examPricing'
import {
  authListVariant,
  listCacheKey,
  readListCache,
  writeListCache,
  clearListCache
} from '@/utils/listPageCache'

function initialCourseState() {
  const cacheKey = listCacheKey('course', authListVariant())
  const cached = readListCache(cacheKey)
  const courses = cached?.courses ? [...cached.courses] : []
  return {
    courses,
    cacheKey,
    loading: courses.length === 0
  }
}

export default {
  name: 'KnowledgeCoursesPage',
  components: {
    ImageBar,
    AdminPagination,
    ContentTagFilterBar,
    Clock,
    Document,
    Refresh,
    RefreshLeft
  },
  data() {
    const initial = initialCourseState()
    return {
      courses: initial.courses,
      courseCacheKey: initial.cacheKey,
      loading: initial.loading,
      refreshing: false,
      searchKeyword: '',
      selectedCategory: '',
      selectedLevel: '',
      selectedTagId: 0,
      currentPage: 1,
      pageSize: 12,
      previewDialogVisible: false,
      previewCourse: null,
      redeemVisible: false,
      redeemTarget: null,
      redeemCode: '',
      redeeming: false
    }
  },
  computed: {
    isLoggedIn() {
      return !!localStorage.getItem('jwt_token')
    },
    categories() {
      const categories = [...new Set(this.courses.map(course => course.category).filter(Boolean))]
      return categories.sort()
    },
    filteredCourses() {
      let filtered = this.courses
      
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase()
        filtered = filtered.filter(course => 
          course.name.toLowerCase().includes(keyword) ||
          (course.description && course.description.toLowerCase().includes(keyword))
        )
      }
      
      if (this.selectedCategory) {
        filtered = filtered.filter(course => course.category === this.selectedCategory)
      }
      
      if (this.selectedLevel) {
        filtered = filtered.filter(course => course.level === parseInt(this.selectedLevel))
      }

      if (this.selectedTagId) {
        const tagId = Number(this.selectedTagId)
        filtered = filtered.filter(course => (course.tagIds || []).some((id) => Number(id) === tagId))
      }
      
      return filtered
    },
    totalPages() {
      return Math.ceil(this.filteredCourses.length / this.pageSize)
    },
    paginatedCourses() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredCourses.slice(start, end)
    }
  },
  async created() {

    
    await this.loadCourses()
  },
  methods: {
    coursePath(kcId) {
      return withCouponQuery(`/knowledge/courses/display/${kcId}`, couponFromRouteQuery(this.$route.query))
    },
    async loadCourses(force = false) {
      const cacheKey = listCacheKey('course', authListVariant())
      this.courseCacheKey = cacheKey
      const hadCourses = this.courses.length > 0
      try {
        if (force || !hadCourses) {
          this.loading = true
        }
        const token = localStorage.getItem('jwt_token')

        console.log("token", token)
        
        // Check if user is logged in
        if (token) {
          // User is logged in, use authenticated endpoint
          const response = await fetch('/api/v1/knowledge/courses/list', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              page: 1,
              pageSize: 1000, // 获取所有课程
              keyword: ''
            })
          })
          
          const data = await response.json()
          if (data.code === 200) {
            this.courses = data.data.list || []
            writeListCache(cacheKey, { courses: this.courses })
          } else if (data.code === 401) {
            this.$message.error('登录已过期，请重新登录')
          } else {
            this.$message.error(data.message || '获取课程列表失败')
          }
        } else {
          // User is not logged in, use guest endpoint
          const response = await fetch('/api/v1/knowledge/courses/guest/list', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              page: 1,
              pageSize: 1000, // 获取所有课程
              keyword: ''
            })
          })
          
          const data = await response.json()
          if (data.code === 200) {
            this.courses = data.data.list || []
            writeListCache(cacheKey, { courses: this.courses })
          } else {
            this.$message.error(data.message || '获取课程列表失败')
          }
        }
      } catch (error) {
        console.error('Failed to load courses:', error)
        this.$message.error('获取课程列表失败')
      } finally {
        this.loading = false
      }
    },
    
    handleSearch() {
      this.currentPage = 1
    },
    
    handleFilter() {
      this.currentPage = 1
    },

    selectCourseTag(tagId) {
      this.selectedTagId = Number(tagId) || 0
      this.currentPage = 1
    },

    async refreshCourses() {
      clearListCache(this.courseCacheKey)
      this.refreshing = true
      try {
        await this.loadCourses(true)
        ElMessage.success('课程列表已刷新')
      } finally {
        this.refreshing = false
      }
    },

    resetFilters() {
      this.searchKeyword = ''
      this.selectedCategory = ''
      this.selectedLevel = ''
      this.selectedTagId = 0
      this.currentPage = 1
      ElMessage.success('筛选已复位')
    },
    
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
    },
    
    handleCurrentChange(page) {
      this.currentPage = page
    },
    
    courseActionLabel(course) {
      return coursePrimaryActionLabel(course, isLoggedIn())
    },

    canRedeem(course) {
      return isLoggedIn() && course && course.fee > 0 && !(course.isAcquired > 0)
    },

    openRedeem(course) {
      this.redeemTarget = course
      this.redeemCode = ''
      this.redeemVisible = true
    },

    async submitRedeem() {
      const code = (this.redeemCode || '').trim()
      if (!this.redeemTarget || !code) {
        this.$message.warning('请填写兑换码')
        return
      }
      this.redeeming = true
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/courses/redeem', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ kcId: this.redeemTarget.kcId, code })
        })
        const data = await response.json()
        if (data.code === 200) {
          this.$message.success(data.message || '兑换成功，可以开始学习')
          this.redeemVisible = false
          this.redeemTarget.isAcquired = 1
          this.redeemTarget.acquireSource = 'redeem'
          this.$router.push(this.coursePath(this.redeemTarget.kcId))
        } else {
          this.$message.error(data.message || '核销失败')
        }
      } catch (e) {
        this.$message.error('核销失败')
      } finally {
        this.redeeming = false
      }
    },

    async viewCourse(course) {
      if (isFreeCourse(course)) {
        this.$router.push(this.coursePath(course.kcId))
        return
      }

      const canProceed = await this.ensureLoginForCourse(
        course,
        '登录后可以购买课程，或使用创作者发给你的兑换码学习。是否前往登录？'
      )
      if (!canProceed) {
        return
      }

      const token = localStorage.getItem('jwt_token')
      
      // Check if course is already purchased
      if (course.isAcquired > 0) {
        // Already purchased, proceed to course display
        this.$router.push(this.coursePath(course.kcId))
        return
      }
      
      // Check if course is free or if user needs to buy it
      if (course.fee > 0) {
        // Show confirmation dialog for paid courses
        try {
          await ElMessageBox.confirm(
            `确定要购买此课程吗？费用：${this.formatPrice(course.fee)}`,
            '购买确认',
            {
              confirmButtonText: '确定购买',
              cancelButtonText: '取消',
              type: 'info',
            }
          )
        } catch (error) {
          if (error === 'cancel') {
            return
          }
        }

        try {
          // First check user balance
          const balanceResponse = await fetch('/api/v1/user/balance', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
          
          const balanceData = await balanceResponse.json()
          if (balanceData.code === 200 && balanceData.data && balanceData.data.length > 0) {
            const userBalance = balanceData.data[0].balance // Balance in cents
            const courseFee = course.fee // Course fee in cents
            
            if (userBalance < courseFee) {
              this.$message.error(`余额不足，需要 ${this.formatPrice(course.fee)}，当前余额 ${this.formatPrice(userBalance)}，请先充值`)
              this.$router.push('/recharge')
              return
            }
          }

          // Proceed with purchase
          const response = await fetch('/api/v1/knowledge/courses/buy', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              kcId: course.kcId
            })
          })
          
          const data = await response.json()
          if (data.code === 200) {
            this.$message.success('购买成功，开始学习')
            this.$router.push(this.coursePath(course.kcId))
          } else {
            this.$message.error(data.message || '购买失败')
          }
        } catch (error) {
          console.error('Error buying course:', error)
          if (error.response?.status === 401) {
            this.$message.error('请先登录')
            goToLogin(this.$router, this.coursePath(course.kcId))
          } else if (error.response?.status === 402) {
            this.$message.error('余额不足，请先充值')
            this.$router.push('/recharge')
          } else {
            this.$message.error('购买失败，请重试')
          }
        }
      } else {
        // Free course, start directly
        this.$router.push(this.coursePath(course.kcId))
      }
    },
    
    showCoursePreview(course) {
      this.previewCourse = {
        ...course,
        kpTree: course.kpTree ? JSON.parse(course.kpTree) : { children: [] }
      }
      this.previewDialogVisible = true
    },
    
    handlePreviewClose() {
      this.previewDialogVisible = false
      this.previewCourse = null
    },

    async ensureLoginForCourse(course, message) {
      if (isLoggedIn()) {
        return true
      }
      const returnPath = this.coursePath(course.kcId)
      const result = await promptLogin(message)
      if (result === 'navigate') {
        this.$router.push(loginPageRoute(returnPath))
      }
      return false
    },
    
    async startLearning() {
      if (this.previewCourse) {
        const canProceed = await this.ensureLoginForCourse(
          this.previewCourse,
          '需要登录后才能开始学习，是否前往登录？'
        )
        if (!canProceed) {
          return
        }

        const token = localStorage.getItem('jwt_token')
        
        // Check if course is already purchased
        if (this.previewCourse.isAcquired > 0) {
          // Already purchased, proceed to course display
          this.viewCourse(this.previewCourse)
          this.previewDialogVisible = false
          return
        }
        
        // Check if course is free or if user needs to buy it
        if (this.previewCourse.fee > 0) {
          // Show confirmation dialog for paid courses
          try {
            await ElMessageBox.confirm(
              `确定要购买此课程吗？费用：${this.formatPrice(this.previewCourse.fee)}`,
              '购买确认',
              {
                confirmButtonText: '确定购买',
                cancelButtonText: '取消',
                type: 'info',
              }
            )
          } catch (error) {
            if (error === 'cancel') {
              return
            }
          }

          try {
            // First check user balance
            const balanceResponse = await fetch('/api/v1/user/balance', {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            })
            
            const balanceData = await balanceResponse.json()
            if (balanceData.code === 200 && balanceData.data && balanceData.data.length > 0) {
              const userBalance = balanceData.data[0].balance // Balance in cents
              const courseFee = this.previewCourse.fee // Course fee in cents
              
              if (userBalance < courseFee) {
                this.$message.error(`余额不足，需要 ${this.formatPrice(this.previewCourse.fee)}，当前余额 ${this.formatPrice(userBalance)}，请先充值`)
                this.$router.push('/recharge')
                return
              }
            }

            // Proceed with purchase
            const response = await fetch('/api/v1/knowledge/courses/buy', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                kcId: this.previewCourse.kcId
              })
            })
            
            const data = await response.json()
            if (data.code === 200) {
              this.$message.success('购买成功，开始学习')
              this.previewDialogVisible = false
              this.viewCourse(this.previewCourse)
            } else {
              this.$message.error(data.message || '购买失败')
            }
          } catch (error) {
            console.error('Error buying course:', error)
            if (error.response?.status === 401) {
              this.$message.error('请先登录')
              goToLogin(this.$router, this.coursePath(this.previewCourse.kcId))
            } else if (error.response?.status === 402) {
              this.$message.error('余额不足，请先充值')
              this.$router.push('/recharge')
            } else {
              this.$message.error('购买失败，请重试')
            }
          }
        } else {
          // Free course, start directly
          this.previewDialogVisible = false
          this.viewCourse(this.previewCourse)
        }
      }
    },
    
    getLevelText(level) {
      const levels = {
        1: '初级',
        2: '中级',
        3: '高级'
      }
      return levels[level] || '未知'
    },
    
    getLevelTagType(level) {
      const types = {
        1: 'success',
        2: 'warning',
        3: 'danger'
      }
      return types[level] || 'info'
    },
    
    getCourseDuration(course) {
      if (!course.kpTree) return '未知'
      try {
        const tree = JSON.parse(course.kpTree)
        const nodeCount = this.countNodes(tree)
        return Math.max(30, nodeCount * 15) // 每个知识点约15分钟
      } catch {
        return '未知'
      }
    },
    
    getKnowledgePointCount(course) {
      if (!course.kpTree) return 0
      try {
        const tree = JSON.parse(course.kpTree)
        return this.countNodes(tree)
      } catch {
        return 0
      }
    },
    
    countNodes(tree) {
      if (!tree.children) return 0
      let count = tree.children.length
      tree.children.forEach(child => {
        if (child.children) {
          count += this.countNodes(child)
        }
      })
      return count
    },
    
    formatPrice(fee) {
      if (!fee || fee === 0) return '免费'
      // 将分转换为元，保留两位小数
      const yuan = fee / 100
      return `¥${yuan.toFixed(2)}`
    },

    getCourseBanner(course) {
      // 优先使用 course.banner 字段
      if (course.banner) return course.banner
      // 随机选一张默认图片
      const defaultBanners = [
        require('@/assets/default-image.jpg'),
        require('@/assets/photogrid.jpg'),
        require('@/assets/coolpeople.png'),
        require('@/assets/logo.png'),
        require('@/assets/texture-bw.png'),
      ]
      // 用 kcId 做伪随机，保证同一课程每次显示一致
      const idx = course.kcId ? (parseInt(course.kcId, 10) % defaultBanners.length) : Math.floor(Math.random() * defaultBanners.length)
      return defaultBanners[idx]
    }
  }
}
</script>

<style scoped>
.knowledge-courses-page {
  min-height: 100vh;
  background: #f5f7fa;
}

.courses-container {
  max-width: 1800px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-header h1 {
  font-size: 32px;
  color: #2c3e50;
  margin-bottom: 8px;
}

.subtitle {
  color: #7f8c8d;
  font-size: 16px;
  margin: 0;
}

.filter-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.filter-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 4px;
}

.loading-container {
  padding: 40px;
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
}

.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  margin-bottom: 30px;
}

.course-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.course-image {
  position: relative;
  height: 160px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.course-banner {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  opacity: 0.85;
  transition: opacity 0.3s;
}
.course-cover {
  position: relative;
  z-index: 1;
}

.course-icon {
  font-size: 48px;
  color: white;
}

.course-content {
  padding: 16px 20px 20px;
}

.course-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  min-width: 0;
}

.course-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  line-height: 1.4;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.course-title-row .el-tag {
  flex-shrink: 0;
}

.course-title-row .el-tag.content-tag {
  cursor: pointer;
}

.course-description {
  color: #7f8c8d;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 16px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.course-meta {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #95a5a6;
  font-size: 12px;
}

.course-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.course-price {
  display: flex;
  align-items: center;
  gap: 4px;
}

.price-label {
  font-size: 12px;
  color: #7f8c8d;
}

.price-value {
  font-size: 16px;
  font-weight: 600;
  color: #e74c3c;
}

.price-value.free {
  color: #67C23A;
}

.price-value .purchased {
  color: #67C23A;
  font-weight: 600;
}

.course-actions {
  display: flex;
  gap: 8px;
}

.course-card.purchased {
  border: 2px solid #67C23A;
}

.course-card.purchased .course-image {
  background: linear-gradient(135deg, #67C23A 0%, #85ce61 100%);
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

/* 大纲对话框样式 */
.course-preview {
  max-height: 60vh;
  overflow-y: auto;
}

.preview-cover {
  width: 100%;
  height: 180px;
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  margin-bottom: 0;
}

.preview-banner {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.preview-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.preview-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.preview-title-row h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 20px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-title-row .el-tag {
  flex-shrink: 0;
}

.preview-price {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
}

.preview-description {
  margin-bottom: 20px;
}

.preview-description h4 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 16px;
}

.preview-description p {
  margin: 0;
  color: #7f8c8d;
  line-height: 1.6;
}

.preview-structure h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 16px;
}

.structure-tree {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.structure-node {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
}

.node-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.node-icon {
  font-size: 16px;
}

.node-name {
  flex: 1;
  font-weight: 500;
  color: #2c3e50;
}

.node-seq {
  background: #e9ecef;
  color: #6c757d;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

.node-children {
  margin-left: 20px;
  padding-left: 10px;
  border-left: 2px solid #dee2e6;
}

.child-node {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}

.child-icon {
  font-size: 14px;
}

.child-name {
  flex: 1;
  color: #6c757d;
  font-size: 14px;
}

.child-seq {
  background: #e9ecef;
  color: #6c757d;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

.no-structure {
  text-align: center;
  padding: 20px;
  color: #6c757d;
}

.purchased-label {
  color: #67C23A;
  font-size: 13px;
  margin-left: 4px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .filter-section {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
    margin-bottom: 8px;
    padding: 8px;
    border-radius: 0;
  }
  
  .filter-controls {
    justify-content: space-between;
  }
  
  .courses-grid {
    grid-template-columns: 1fr;
  }
  
  .courses-container {
    padding: 0;
  }

  .knowledge-courses-page {
    background: var(--cp-page-bg, #f7f8fa);
  }
  
  .page-header h1 {
    font-size: 24px;
  }
  
  .course-footer {
    flex-direction: column;
  }
}
</style> 